import { useState, useEffect } from 'react';
import { HelpTip, IncidentRow, Loading, PANEL_HELP, api, useToast, PanelHowTo } from './_barrel.js';


        function BreachExposurePanel({ token, subjectId, onIncidentsChanged, openDialog }) {
            const [emails, setEmails] = useState(null);
            const [breachIncidents, setBreachIncidents] = useState([]);
            const [adding, setAdding] = useState(false);
            const [checking, setChecking] = useState(false);
            const [newEmail, setNewEmail] = useState('');
            const [error, setError] = useState(null);
            const [lastResult, setLastResult] = useState(null);
            const [open, setOpen] = useState(false);
            const toast = useToast();

            const BREACH_TYPES = ['breach','breach_exposure','credential_breach','data_breach'];
            const reload = async () => {
                try {
                    const data = await api(`/api/v1/subjects/${subjectId}/emails`, {}, token);
                    setEmails(data);
                } catch (e) { setError(e.message); }
                try {
                    const inc = await api(`/api/v1/incidents?subject_id=${subjectId}&limit=200`, {}, token);
                    const items = (inc && inc.items) ? inc.items : (Array.isArray(inc) ? inc : []);
                    setBreachIncidents(items.filter(i => BREACH_TYPES.includes(i.incident_type_code)));
                } catch (e) { /* leave breach list as-is on error */ }
            };

            useEffect(() => { reload(); }, [token, subjectId]);

            const submitAdd = async (e) => {
                e?.preventDefault();
                if (!newEmail.trim()) return;
                setAdding(true);
                setError(null);
                try {
                    await api(
                        `/api/v1/subjects/${subjectId}/emails`,
                        { method: 'POST', body: JSON.stringify({ email: newEmail.trim().toLowerCase() }) },
                        token
                    );
                    setNewEmail('');
                    await reload();
                    toast('Email added');
                } catch (err) {
                    setError(err.message);
                } finally {
                    setAdding(false);
                }
            };

            const removeEmail = async (emailId) => {
                if (!confirm('Stop monitoring this email? Historic incidents will remain.')) return;
                try {
                    await api(`/api/v1/subjects/${subjectId}/emails/${emailId}`, { method: 'DELETE' }, token);
                    await reload();
                    toast('Email removed');
                } catch (err) { setError(err.message); }
            };

            const runCheck = async () => {
                setChecking(true);
                setError(null);
                setLastResult(null);
                try {
                    const result = await api(
                        `/api/v1/subjects/${subjectId}/emails/check`,
                        { method: 'POST' },
                        token
                    );
                    setLastResult(result);
                    await reload();
                    if (result.new_incidents_created > 0) {
                        toast(`${result.new_incidents_created} new breach exposure(s) found and added as draft incidents`);
                        onIncidentsChanged?.();
                    } else {
                        toast(result.note || 'Check complete. No new exposures.');
                    }
                } catch (err) {
                    setError(err.message);
                } finally {
                    setChecking(false);
                }
            };

            return (
                <div className="panel mb-6">
                    <div className="panel-header is-self-collapse flex justify-between items-center cursor-pointer" onClick={() => setOpen(!open)}>
                        <div>
                            <div className="font-display text-xl gold">Breach Exposure Check<HelpTip text={PANEL_HELP.breach_exposure} /></div>
                            <div className="text-xs text-muted font-mono mt-1">
                                Supporting check · Have I Been Pwned
                                {!open && breachIncidents.length > 0 && <span> · {breachIncidents.length} breach incident(s)</span>}
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                className="btn-ghost"
                                onClick={(e) => { e.stopPropagation(); setOpen(true); runCheck(); }}
                                disabled={checking || !emails || emails.length === 0}
                            >
                                {checking ? 'Checking…' : 'Run Breach Check'}
                            </button>
                            <div className="text-xs text-muted">{open ? '▾' : '▸'}</div>
                        </div>
                    </div>
                    <PanelHowTo id="breach_exposure" />

                    {open && (
                    <div className="p-5">
                        {error && <div className="text-[var(--red)] text-sm mb-3">{error}</div>}
                        {lastResult && lastResult.new_incidents_created === 0 && (
                            <div className="text-xs text-muted font-mono mb-3">
                                Last run: {lastResult.emails_checked} email(s) checked · {lastResult.total_breaches_found} breach(es) seen · no new incidents
                            </div>
                        )}

                        <div className="mb-5 p-3 text-sm" style={{ background: 'var(--zone-2)', border: '1px solid var(--rule-2)', borderRadius: 'var(--radius-sm)', lineHeight: 1.55 }}>
                            <div className="mb-1" style={{ fontWeight: 600 }}>What this means</div>
                            <div className="text-soft">
                                This checks the principal's monitored email addresses against known data breaches. Each hit means their credentials or personal data were exposed in that breach — a risk of <strong>account takeover</strong> and <strong>targeted phishing</strong>, and, through password reuse, a way into current email, banking and social accounts. For a protected principal that can escalate from an online problem to a physical-security one.
                            </div>
                        </div>

                        {breachIncidents.length > 0 && (
                            <div className="mb-5">
                                <div className="text-xs uppercase tracking-widest text-muted font-mono mb-2">
                                    Breach incidents ({breachIncidents.length}){(() => { const isNew = (x) => x.status === 'draft' && x.occurred_at && new Date(x.occurred_at).getFullYear() >= 2026; const n = breachIncidents.filter(isNew).length; return n > 0 ? <span> · <span style={{color:'var(--amber, #f59e0b)', fontWeight:700}}>{n} new</span></span> : null; })()} · all statuses · click to review or re-edit
                                </div>
                                <div className="border border-veil rounded">
                                    {breachIncidents
                                        .slice()
                                        .sort((a,b) => new Date(b.detected_at) - new Date(a.detected_at))
                                        .map(i => (
                                            <div key={i.id} style={(i.status === 'draft' && i.occurred_at && new Date(i.occurred_at).getFullYear() >= 2026) ? {borderLeft:'3px solid var(--amber, #f59e0b)', position:'relative'} : {position:'relative'}}>
                                                {i.status === 'draft' && i.occurred_at && new Date(i.occurred_at).getFullYear() >= 2026 && (
                                                    <span style={{position:'absolute', top:'6px', right:'8px', zIndex:5, background:'var(--amber, #f59e0b)', color:'var(--bg)', fontSize:'9px', fontWeight:700, letterSpacing:'0.1em', padding:'1px 5px', borderRadius:'3px'}}>NEW</span>
                                                )}
                                                <IncidentRow
                                                    incident={i}
                                                    token={token}
                                                    onSignoff={() => openDialog && openDialog('signoff', { incident: i, onSuccess: reload })}
                                                />
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}

                        {breachIncidents.length > 0 && (
                            <div className="mb-5 p-3 text-sm" style={{ background: 'var(--zone-2)', border: '1px solid var(--rule-2)', borderRadius: 'var(--radius-sm)', lineHeight: 1.55 }}>
                                <div className="mb-2" style={{ fontWeight: 600 }}>So what — recommended actions</div>
                                <ul style={{ listStyle: 'disc', paddingLeft: '18px' }} className="text-soft">
                                    <li style={{ marginBottom: '4px' }}>Change the password anywhere the principal reused a breached password, and treat it as compromised.</li>
                                    <li style={{ marginBottom: '4px' }}>Turn on multi-factor authentication (MFA) on email, banking and social accounts.</li>
                                    <li style={{ marginBottom: '4px' }}>Move to a password manager so every account has a unique password.</li>
                                    <li style={{ marginBottom: '4px' }}>Brief the principal on phishing and SIM-swap attempts that may use the leaked details.</li>
                                    <li>Consider ongoing identity/credit monitoring for high-exposure principals.</li>
                                </ul>
                            </div>
                        )}

                        {!emails ? (
                            <div className="text-muted text-sm">Loading…</div>
                        ) : emails.length === 0 ? (
                            <div className="text-muted text-sm mb-4">
                                No email addresses are being monitored. Add one to enable breach checks for this subject.
                            </div>
                        ) : (
                            <div className="mb-4">
                                {emails.map(em => (
                                    <div key={em.id} className="flex justify-between items-center py-2 border-b border-veil last:border-b-0">
                                        <div>
                                            <div className="font-mono text-sm">{em.email}</div>
                                            <div className="text-xs text-muted font-mono">
                                                {em.last_checked_at
                                                    ? `Last checked ${new Date(em.last_checked_at).toLocaleString('en-GB')} · ${em.check_count} run(s)`
                                                    : 'Not yet checked'}
                                            </div>
                                        </div>
                                        <button
                                            className="text-xs text-muted hover:text-[var(--red)] uppercase tracking-widest"
                                            onClick={() => removeEmail(em.id)}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        <form onSubmit={submitAdd} className="flex gap-2 mt-2">
                            <input
                                type="email"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                placeholder="email@example.com"
                                disabled={adding}
                                style={{flex: 1}}
                            />
                            <button type="submit" className="btn-ghost" disabled={adding || !newEmail}>
                                {adding ? 'Adding…' : '+ Add Email'}
                            </button>
                        </form>
                        <div className="text-xs text-dim font-mono mt-3">
                            Emails are checked against Have I Been Pwned. New breach exposures since the last run are created as draft incidents for analyst review and sign-off.
                        </div>
                    </div>
                    )}
                </div>
            );
        }

export { BreachExposurePanel };
